/**
 * Requirement Detail Panel Component
 * Right panel showing full requirement details, coverage, and cross-framework mappings
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  AlertTriangle,
  Upload,
  Plus,
  ExternalLink,
  Shield,
  Book,
  Network,
  FileText,
  Eye,
  User,
  RefreshCw
} from 'lucide-react';
import { API } from '../../App';
import { apiFetch } from '../../services/api_service';


export default function RequirementDetail({ requirement, frameworkCode, coverage }) {
  const [crossFrameworkMappings, setCrossFrameworkMappings] = useState([]);
  const [loadingMappings, setLoadingMappings] = useState(false);

  // Fetch cross-framework mappings when requirement changes
  useEffect(() => {
    if (!requirement?.id) return;
    
    // If mappings already exist in requirement, use them
    if (requirement.equivalentRequirements && requirement.equivalentRequirements.length > 0) {
      setCrossFrameworkMappings(requirement.equivalentRequirements);
      return;
    }

    // Otherwise fetch from API
    const fetchMappings = async () => {
      setLoadingMappings(true);
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      try {
        const res = await fetch(
          `${API}/v1/requirements/${frameworkCode}/cross-framework/${requirement.id}`,
          { headers }
        );
        if (res.ok) {
          const data = await res.json();
          const mappings = (data.mappings || []).map(m => ({
            frameworkCode: m.framework_code,
            requirementCode: m.requirement_code,
            requirementTitle: m.requirement_title,
            equivalenceConfidence: m.equivalence_confidence
          }));
          setCrossFrameworkMappings(mappings);
        }
      } catch (err) {
        console.error('Error fetching cross-framework mappings:', err);
      } finally {
        setLoadingMappings(false);
      }
    };

    fetchMappings();
  }, [requirement?.id, frameworkCode, requirement?.equivalentRequirements]);

  if (!requirement) return null;

  // Use fetched mappings or existing ones
  const mappings = crossFrameworkMappings.length > 0 
    ? crossFrameworkMappings 
    : requirement.equivalentRequirements || [];

  return (
    <div className="space-y-6" data-testid="requirement-detail">
      {/* Header Card */}
      <div className="bg-[#0D0D0D] border border-[#1A1A1A] rounded-xl p-6">
        {/* Header Row */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-1 text-xs font-mono font-medium bg-[#1A1A1A] text-[#A0A0A0] rounded">
                {requirement.requirementCode}
              </span>
              {requirement.isMandatory ? (
                <span className="px-2 py-1 text-xs font-medium bg-blue-500/20 text-blue-400 rounded">
                  Mandatory
                </span>
              ) : (
                <span className="px-2 py-1 text-xs font-medium bg-[#1A1A1A] text-[#6B6B6B] rounded">
                  Conditional
                </span>
              )}
            </div>
            <h2 className="text-xl font-semibold text-white">
              {requirement.requirementTitle}
            </h2>
          </div>

          {/* Status Badge */}
          {coverage && (
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${
              coverage.status === 'complete' 
                ? 'bg-emerald-500/20 text-emerald-400' 
                : coverage.status === 'partial'
                  ? 'bg-amber-500/20 text-amber-400'
                  : 'bg-[#1A1A1A] text-[#6B6B6B]'
            }`}>
              {coverage.status === 'complete' && <CheckCircle2 size={16} />}
              {coverage.status === 'partial' && <AlertTriangle size={16} />}
              {coverage.status === 'complete' ? 'Complete' : 
               coverage.status === 'partial' ? 'Partially Complete' : 'Not Started'}
            </div>
          )}
        </div>

        {/* Meta Info */}
        <div className="flex items-center gap-4 text-sm text-[#6B6B6B] mb-4">
          {coverage?.isMaterial && (
            <MaterialityBadge type={coverage.materialityType} />
          )}
          {requirement.dataType && (
            <div className="flex items-center gap-1.5">
              <FileText size={14} />
              <span>Type: {requirement.dataType}</span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <Shield size={14} />
            <span>Assurance: Limited</span>
          </div>
        </div>

        {/* Description */}
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-white mb-2">Description</h4>
          <p className="text-sm text-[#A0A0A0] leading-relaxed">
            {requirement.requirementDescription}
          </p>

          {/* Plain language explanation */}
          {requirement.plainLanguageExplanation && (
            <div className="mt-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <p className="text-xs font-medium text-blue-400 mb-1">Plain English:</p>
              <p className="text-sm text-blue-200 leading-relaxed">
                {requirement.plainLanguageExplanation}
              </p>
            </div>
          )}
        </div>

        {/* Data Requirements */}
        {requirement.quantitativeSpecs && (
          <>
            <div className="border-t border-[#1A1A1A] my-4" />
            <div>
              <h4 className="text-sm font-semibold text-white mb-2">Data Requirements</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-[#6B6B6B]">Unit:</span>
                  <span className="ml-2 font-medium text-white">{requirement.quantitativeSpecs.unit}</span>
                </div>
                <div>
                  <span className="text-[#6B6B6B]">Precision:</span>
                  <span className="ml-2 font-medium text-white">
                    {requirement.quantitativeSpecs.precision} decimal{requirement.quantitativeSpecs.precision !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Your Coverage Card */}
      <div className="bg-[#0D0D0D] border border-[#1A1A1A] rounded-xl p-6">
        <h3 className="text-base font-semibold text-white flex items-center gap-2 mb-4">
          <CheckCircle2 size={18} className="text-emerald-400" />
          Your Coverage
        </h3>

        {/* Evidence */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-[#A0A0A0]">
              Evidence ({coverage?.evidenceItems?.length || 0})
            </h4>
            <Link
              to={`/evidence/upload?requirement=${requirement.id}`}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 rounded-lg transition-colors"
            >
              <Upload size={12} />
              Upload
            </Link>
          </div>

          {coverage?.evidenceItems && coverage.evidenceItems.length > 0 ? (
            <div className="space-y-2">
              {coverage.evidenceItems.map((evidence, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-3 rounded-lg border border-[#1A1A1A] hover:border-emerald-500/30 hover:bg-[#111111] transition-colors group"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <FileText size={16} className="text-[#4A4A4A] shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-white truncate">
                        {evidence.fileName}
                      </p>
                      <p className="text-xs text-[#6B6B6B]">
                        Uploaded {new Date(evidence.uploadedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {evidence.owner && (
                      <div className="flex items-center gap-1 text-xs text-[#6B6B6B]">
                        <User size={12} />
                        <span>{evidence.owner.name}</span>
                      </div>
                    )}
                    <Link
                      to={`/evidence/${evidence.id}`}
                      className="p-1.5 text-[#6B6B6B] hover:text-emerald-400 transition-colors"
                    >
                      <Eye size={14} />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 border border-dashed border-[#2A2A2A] rounded-lg">
              <p className="text-sm text-[#6B6B6B] mb-2">No evidence uploaded yet</p>
              <Link
                to={`/evidence/upload?requirement=${requirement.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 rounded-lg transition-colors"
              >
                <Upload size={12} />
                Upload Evidence
              </Link>
            </div>
          )}
        </div>

        <div className="border-t border-[#1A1A1A] my-4" />

        {/* Claims */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-[#A0A0A0]">
              Claims ({coverage?.claims?.length || 0})
            </h4>
            <Link
              to={`/claims-assertions?requirement=${requirement.id}`}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 rounded-lg transition-colors"
            >
              <Plus size={12} />
              Create Claim
            </Link>
          </div>

          {coverage?.claims && coverage.claims.length > 0 ? (
            <div className="space-y-2">
              {coverage.claims.map((claim, idx) => (
                <div 
                  key={idx}
                  className="p-3 rounded-lg border border-[#1A1A1A]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">
                        {claim.claimText}
                      </p>
                      {claim.claimValue && (
                        <p className="text-xs text-[#6B6B6B] mt-1">
                          {claim.claimValue.numeric?.toLocaleString()} {claim.claimValue.unit}
                        </p>
                      )}
                    </div>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                      claim.approvalStatus === 'approved'
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : 'bg-amber-500/20 text-amber-400'
                    }`}>
                      {claim.approvalStatus}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 border border-dashed border-[#2A2A2A] rounded-lg">
              <p className="text-sm text-[#6B6B6B] mb-2">No claims created yet</p>
              <button 
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#6B6B6B] bg-[#1A1A1A] rounded-lg opacity-60"
                disabled={!coverage?.evidenceItems?.length}
              >
                <Plus size={12} />
                Create Claim
              </button>
              {!coverage?.evidenceItems?.length && (
                <p className="text-xs text-[#4A4A4A] mt-2">Upload evidence first</p>
              )}
            </div>
          )}
        </div>

        {/* Used in Cycles */}
        {coverage?.usedInCycles && coverage.usedInCycles.length > 0 && (
          <>
            <div className="border-t border-[#1A1A1A] my-4" />
            <div>
              <h4 className="text-sm font-semibold text-[#A0A0A0] mb-2">
                Used in Disclosure Cycles
              </h4>
              <div className="space-y-1">
                {coverage.usedInCycles.map((cycle, idx) => (
                  <Link
                    key={idx}
                    to={`/cycles/${cycle.id}`}
                    className="flex items-center justify-between p-2 rounded-md hover:bg-[#1A1A1A] text-sm group"
                  >
                    <span className="text-[#A0A0A0] group-hover:text-emerald-400">
                      {cycle.name}
                    </span>
                    <span className="px-2 py-0.5 text-xs bg-[#1A1A1A] text-[#6B6B6B] rounded">
                      {cycle.status}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Cross-Framework Intelligence */}
      {(mappings.length > 0 || loadingMappings) && (
        <div className="bg-[#0D0D0D] border border-teal-500/30 rounded-xl p-6">
          <h3 className="text-base font-semibold text-white flex items-center gap-2 mb-2">
            <Network size={18} className="text-teal-400" />
            Cross-Framework Intelligence
            {loadingMappings && <RefreshCw size={14} className="animate-spin text-teal-400" />}
          </h3>
          <p className="text-sm text-[#6B6B6B] mb-4">
            This requirement is also needed by other frameworks. Upload once, satisfy all.
          </p>

          <div className="space-y-3">
            {mappings.map((equiv, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between p-4 rounded-lg border border-[#1A1A1A] hover:border-teal-500/30 hover:bg-teal-500/5 transition-colors group"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 text-xs font-mono bg-[#1A1A1A] text-[#A0A0A0] rounded">
                      {equiv.frameworkCode}
                    </span>
                    <span className="px-2 py-0.5 text-xs font-mono bg-[#1A1A1A] text-[#A0A0A0] rounded">
                      {equiv.requirementCode}
                    </span>
                  </div>
                  <p className="text-sm text-[#A0A0A0]">
                    {equiv.requirementTitle || 'View requirement details'}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="px-2 py-1 text-xs font-medium bg-teal-500/20 text-teal-400 rounded">
                    {Math.round(equiv.equivalenceConfidence * 100)}% match
                  </span>
                  <ExternalLink size={16} className="text-[#4A4A4A] group-hover:text-teal-400" />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 p-3 rounded-lg bg-teal-500/10 border border-teal-500/20">
            <p className="text-sm text-teal-300 font-medium">
              Upload evidence once for {requirement.requirementCode}, 
              and it automatically satisfies {mappings.length} other framework requirement{mappings.length > 1 ? 's' : ''}.
            </p>
          </div>
        </div>
      )}

      {/* Methodology & Guidance */}
      {requirement.methodology && (
        <div className="bg-[#0D0D0D] border border-[#1A1A1A] rounded-xl p-6">
          <h3 className="text-base font-semibold text-white flex items-center gap-2 mb-4">
            <Book size={18} className="text-blue-400" />
            Methodology & Guidance
          </h3>

          <div className="mb-4">
            <h4 className="text-sm font-semibold text-[#A0A0A0] mb-2">Standard</h4>
            <p className="text-sm text-white">{requirement.methodology.primaryStandard}</p>
          </div>

          {requirement.methodology.acceptedMethodologies && (
            <>
              <div className="border-t border-[#1A1A1A] my-4" />
              <div>
                <h4 className="text-sm font-semibold text-[#A0A0A0] mb-3">Calculation Methods</h4>
                <div className="space-y-3">
                  {requirement.methodology.acceptedMethodologies.map((method, idx) => (
                    <div key={idx} className="p-3 rounded-lg bg-[#0A0A0A] border border-[#1A1A1A]">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <p className="text-sm font-medium text-white">{method.name}</p>
                        <span className="px-2 py-0.5 text-[10px] bg-[#1A1A1A] text-[#6B6B6B] rounded">
                          {method.accuracy} accuracy
                        </span>
                      </div>
                      <p className="text-xs text-[#6B6B6B]">{method.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Common Errors */}
      {requirement.commonErrors && requirement.commonErrors.length > 0 && (
        <div className="bg-amber-500/5 border border-amber-500/30 rounded-xl p-6">
          <h3 className="text-base font-semibold text-white flex items-center gap-2 mb-4">
            <AlertTriangle size={18} className="text-amber-400" />
            Common Pitfalls
          </h3>

          <div className="space-y-3">
            {requirement.commonErrors.map((error, idx) => (
              <div key={idx} className="p-4 rounded-lg bg-[#0D0D0D] border border-[#1A1A1A]">
                <p className="text-sm font-medium text-white mb-1">{error.error}</p>
                <p className="text-xs text-[#6B6B6B] mb-2">{error.description}</p>
                <div className="flex items-start gap-2">
                  <CheckCircle2 size={14} className="text-emerald-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-[#A0A0A0]">
                    <span className="font-medium">Fix:</span> {error.remedy}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Materiality Badge Component
function MaterialityBadge({ type }) {
  const config = {
    impact: { label: 'Impact Material', colorClass: 'bg-purple-500/20 text-purple-400' },
    financial: { label: 'Financially Material', colorClass: 'bg-blue-500/20 text-blue-400' },
    double: { label: 'Double Material', colorClass: 'bg-emerald-500/20 text-emerald-400' }
  };

  const { label, colorClass } = config[type] || config.impact;

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded ${colorClass}`}>
      {label}
    </span>
  );
}
